const MINUTE_MS = 60 * 1000;
const DAY_MINUTES = 24 * 60;

/**
 * Zaokrąglone przedziały „ostatnio widziany” (bez dokładnego czasu z zegara).
 * @param {string | null | undefined} iso
 * @param {Date} [now]
 * @returns {null | {
 *   kind: 'online' | 'about5Min' | 'min15' | 'min30' | 'overHours' | 'overDays' | 'overWeeks' | 'overMonths' | 'overYears',
 *   count?: number,
 * }}
 */
export function getProfileLastSeenDescriptor(iso, now = new Date()) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(+then)) return null;

  const diffMs = now - then;
  const min = diffMs / MINUTE_MS;

  if (diffMs < 0 || min < 5) return { kind: 'online' };
  if (min < 15) return { kind: 'about5Min' };
  if (min < 30) return { kind: 'min15' };
  if (min < 60) return { kind: 'min30' };
  if (min < DAY_MINUTES) {
    const hours = Math.floor(min / 60);
    return { kind: 'overHours', count: Math.min(Math.max(hours, 1), 23) };
  }

  const totalDays = min / DAY_MINUTES;

  if (totalDays < 7) {
    const days = Math.floor(totalDays);
    return { kind: 'overDays', count: Math.max(1, Math.min(days, 6)) };
  }

  if (totalDays < 30) {
    const weeks = Math.floor(totalDays / 7);
    return { kind: 'overWeeks', count: Math.max(1, weeks) };
  }

  if (totalDays < 365) {
    const months = Math.floor(totalDays / 30);
    return { kind: 'overMonths', count: Math.max(1, months) };
  }

  const years = Math.floor(totalDays / 365);
  return { kind: 'overYears', count: Math.max(1, years) };
}

export function isProfileLastSeenOnline(iso, now = new Date()) {
  return getProfileLastSeenDescriptor(iso, now)?.kind === 'online';
}

export function getProfileLastSeenLampTone(iso, now = new Date()) {
  const kind = getProfileLastSeenDescriptor(iso, now)?.kind;
  if (kind === 'online') return 'online';
  if (kind === 'about5Min' || kind === 'min15' || kind === 'min30') {
    return 'recent';
  }
  return 'offline';
}

function plPickHours(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneHour');
  if (n === 2) return t('profile.popup.lastSeenOverTwoHours');
  if (n === 3 || n === 4) return t('profile.popup.lastSeenOverHours34', { count: n });
  return t('profile.popup.lastSeenOverHoursMany', { count: n });
}

function enPickHours(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneHour');
  if (n === 2) return t('profile.popup.lastSeenOverTwoHours');
  return t('profile.popup.lastSeenOverHoursMany', { count: n });
}

function plPickDays(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneDay');
  if (n === 2) return t('profile.popup.lastSeenOverTwoDays');
  if (n === 3 || n === 4) return t('profile.popup.lastSeenOverDays34', { count: n });
  return t('profile.popup.lastSeenOverDays56', { count: n });
}

function enPickDays(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneDay');
  if (n === 2) return t('profile.popup.lastSeenOverTwoDays');
  return t('profile.popup.lastSeenOverDaysMany', { count: n });
}

function plPickWeeks(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneWeek');
  const m10 = n % 10;
  const m100 = n % 100;
  if (n === 2) return t('profile.popup.lastSeenOverWeeks2');
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) {
    return t('profile.popup.lastSeenOverWeeksFew', { count: n });
  }
  return t('profile.popup.lastSeenOverWeeksMany', { count: n });
}

function enPickWeeks(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneWeek');
  if (n === 2) return t('profile.popup.lastSeenOverWeeks2');
  return t('profile.popup.lastSeenOverWeeksMany', { count: n });
}

function plPickMonths(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneMonth');
  if (n === 2) return t('profile.popup.lastSeenOverMonths2');
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) {
    return t('profile.popup.lastSeenOverMonthsFew', { count: n });
  }
  return t('profile.popup.lastSeenOverMonthsMany', { count: n });
}

function enPickMonths(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneMonth');
  return n === 2
    ? t('profile.popup.lastSeenOverMonths2')
    : t('profile.popup.lastSeenOverMonthsMany', { count: n });
}

function plPickYears(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneYear');
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) {
    return t('profile.popup.lastSeenOverYearsFew', { count: n });
  }
  return t('profile.popup.lastSeenOverYearsMany', { count: n });
}

function enPickYears(t, n) {
  if (n === 1) return t('profile.popup.lastSeenOverOneYear');
  return t('profile.popup.lastSeenOverYearsMany', { count: n });
}

/**
 * Krótka fraza statusu „ostatnio widziany” (bez etykiety nagłówkowej).
 * @param {string} last_seen_at
 * @param {import('i18next').TFunction} t
 * @param {{ language?: string }} i18n
 * @returns {string | null}
 */
export function formatProfileLastSeenPhrase(last_seen_at, t, i18n) {
  const desc = getProfileLastSeenDescriptor(last_seen_at);
  if (!desc) return null;
  const pl = (i18n.language || '').toLowerCase().startsWith('pl');

  switch (desc.kind) {
    case 'online':
      return t('profile.popup.lastSeenOnline');
    case 'about5Min':
      return t('profile.popup.lastSeenAbout5Min');
    case 'min15':
      return t('profile.popup.lastSeen15Min');
    case 'min30':
      return t('profile.popup.lastSeen30Min');
    case 'overHours': {
      const n = desc.count ?? 1;
      return pl ? plPickHours(t, n) : enPickHours(t, n);
    }
    case 'overDays': {
      const n = desc.count ?? 1;
      return pl ? plPickDays(t, n) : enPickDays(t, n);
    }
    case 'overWeeks': {
      const n = desc.count ?? 1;
      return pl ? plPickWeeks(t, n) : enPickWeeks(t, n);
    }
    case 'overMonths': {
      const n = desc.count ?? 1;
      return pl ? plPickMonths(t, n) : enPickMonths(t, n);
    }
    case 'overYears': {
      const n = desc.count ?? 1;
      return pl ? plPickYears(t, n) : enPickYears(t, n);
    }
    default:
      return null;
  }
}
